/** @jsx jsx */
import { useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { List } from 'react-virtualized';

import { css, jsx } from '@emotion/react';
import { Button, MenuItem } from '@blueprintjs/core';
import { ItemRenderer, Suggest } from '@blueprintjs/select';

interface Row {
	key: string;
	displayText: string;
	fullName: string;
	group: string;
}
interface CuratedViewLandingProps {
	searchOptions: Array<Row>;
	runQuery: (arg1: string) => void;
}

const OurSuggestions = Suggest.ofType<Row>();

/**
 * The landing page for when the organization isn't set in the url. This gives a search box and drop down for possible
 * entries. Each entry will know what curated view to show (Org show Single org view, Vul show Single vul view etc)
 * Usage example: See /serberus/cyhy/src/curatedViews/index
 * @param p
 * @constructor
 */
export function CuratedViewLanding(p: CuratedViewLandingProps) {
	const history = useHistory();
	const { url } = useRouteMatch();
	const _onClick = ({ key, group }) => {
		const _url = url.slice(-1) === '/' ? url.slice(0, -1) : url;
		history.push(`${_url}/${group}/${encodeURIComponent(key)}`);
	};
	const _areEqual = (a, b) =>
		a.displayText.toLowerCase() === b.displayText.toLowerCase() && a.group.toLowerCase() === b.group.toLowerCase();

	const suggestRef = useRef();

	const [query, setQuery] = useState<string>();
	const _updateQuery = (e: string) => {
		setQuery(e);
	};
	const _clearQuery = () => {
		setQuery('');
	};


	return (
		<div cy-test="curated-views-search" css={typeAheadCss}>
			<OurSuggestions
				ref={suggestRef}
				items={p.searchOptions || []}
				onItemSelect={_onClick}
				inputValueRenderer={({ displayText }) => displayText}
				itemRenderer={filterAndBuildSuggestRow}
				fill={true}
				itemsEqual={_areEqual}
				onQueryChange={(e) => {_updateQuery(e); p.runQuery(e)}}
				itemListRenderer={renderList(suggestRef)}
				openOnKeyDown={true}
				inputProps={{
					leftIcon: 'search',
					autoFocus: true,
					rightElement: <Button cy-test="clear-search" onClick={_clearQuery} icon={'cross'} minimal disabled={false} />,
				}}
				query={query}
			/>
		</div>
	);
}

const renderList =
	(ref) =>
	({ items, renderItem }) => {
		//this renders the list item AND checks each item if it matches the predicate (it thru the chain call filterAndBuildSuggestRow)
		const renderedItems = items.map(renderItem);

		const width = ref.current?.inputElement?.clientWidth;
		//Ref has to be a string. Its never used in our code, but the lib uses it
		return (
			<List
				ref="virtual-list-xyz"
				height={200}
				overscanRowCount={3}
				noRowsRenderer={() => (items.loading ? <div>Loading...</div> : <div>No rows</div>)}
				rowCount={renderedItems.length}
				rowHeight={35}
				rowRenderer={(props) => {
					const { key, style } = props;
					//The row in the list is this div, and the child of this div is result of filterAndBuildSuggestRow
					return (
						<div key={key} css={rowCss} style={style}>
							{renderedItems[props.index]}
						</div>
					);
				}}
				width={width - 10}
			/>
		);
	};

const filterAndBuildSuggestRow: ItemRenderer<Row> = (item, { handleClick, modifiers, query }) => {
	return (
		<MenuItem
			cy-test="curated-views-suggested-row"
			active={modifiers.active}
			disabled={modifiers.disabled}
			label={item.group === 'Id' ? 'Organization' : item.group}
			key={item.fullName}
			onClick={handleClick}
			text={highlightText(item.displayText, query)}
		/>
	);
};

function highlightText(text, query) {
	return <span dangerouslySetInnerHTML={{ __html: text.replace(query, '<strong>$&</strong>') }} />;
}

const typeAheadCss = css`
	display: flex;
`;

const rowCss = css`
	list-style: none;
`;
